import { useState } from "react";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { Save, Plus, X } from "lucide-react";

type Plan = {
  id: string;
  name: string;
  monthly_price: number;
  yearly_price: number;
  yearly_savings_percent: number;
  is_active: boolean;
  features: string[];
  features_en?: string[];
  features_es?: string[];
};

type FeatureLang = 'tr' | 'en' | 'es';

function featuresKey(lang: FeatureLang): 'features' | 'features_en' | 'features_es' {
  return lang === 'en' ? 'features_en' : lang === 'es' ? 'features_es' : 'features';
}

async function fetchPlans(): Promise<Plan[]> {
  const res = await adminFetch("/api/admin/subscription-plans");
  const json = await res.json();
  return json.plans ?? [];
}

async function updatePlan(id: string, body: Partial<Plan>): Promise<Plan> {
  const res = await adminFetch(`/api/admin/subscription-plans/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? "Güncelleme başarısız");
  return json.plan;
}

export default function AdminNumerolojiAbonelikPlanlari() {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const { sessionQuery, loginMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);
  const queryClient = useQueryClient();

  const plansQuery = useQuery({ queryKey: ["subscription-plans"], queryFn: fetchPlans });
  const plans = plansQuery.data ?? [];

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Plan> }) => updatePlan(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["subscription-plans"] }),
  });

  const [drafts, setDrafts] = useState<Record<string, Partial<Plan>>>({});
  const [langTabs, setLangTabs] = useState<Record<string, FeatureLang>>({});

  function getDraft(plan: Plan): Partial<Plan> {
    return drafts[plan.id] ?? {};
  }

  function getLangTab(planId: string): FeatureLang {
    return langTabs[planId] ?? "tr";
  }

  function setField<K extends keyof Plan>(id: string, key: K, value: Plan[K]) {
    setDrafts(prev => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  }

  function getFeatures(plan: Plan, lang: FeatureLang): string[] {
    const key = featuresKey(lang);
    const draft = getDraft(plan);
    return (draft[key] as string[] | undefined) ?? (plan[key] as string[] | undefined) ?? [];
  }

  function setFeature(planId: string, plan: Plan, lang: FeatureLang, idx: number, value: string) {
    const current = getFeatures(plan, lang).slice();
    current[idx] = value;
    setField(planId, featuresKey(lang), current);
  }

  function addFeature(planId: string, plan: Plan, lang: FeatureLang) {
    setField(planId, featuresKey(lang), [...getFeatures(plan, lang), ""]);
  }

  function removeFeature(planId: string, plan: Plan, lang: FeatureLang, idx: number) {
    const current = getFeatures(plan, lang).filter((_, i) => i !== idx);
    setField(planId, featuresKey(lang), current);
  }

  function calcSavings(monthly: number, yearly: number): number {
    if (!monthly || !yearly) return 0;
    return Math.round((1 - (yearly / 12) / monthly) * 100);
  }

  function handleSave(plan: Plan) {
    const draft = getDraft(plan);
    const monthly = Number(draft.monthly_price ?? plan.monthly_price);
    const yearly = Number(draft.yearly_price ?? plan.yearly_price);
    const body: Partial<Plan> = {
      ...draft,
      monthly_price: monthly,
      yearly_price: yearly,
      yearly_savings_percent: calcSavings(monthly, yearly),
      is_active: draft.is_active ?? plan.is_active,
      features: getFeatures(plan, "tr").filter(f => f.trim()),
      features_en: getFeatures(plan, "en").filter(f => f.trim()),
      features_es: getFeatures(plan, "es").filter(f => f.trim()),
    };
    updateMut.mutate({ id: plan.id, body });
  }

  if (sessionQuery.isLoading) return <div className="p-8 text-sm text-muted-foreground">Yükleniyor...</div>;

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <div className="flex items-center gap-3 border-b bg-background/80 px-4 py-3 backdrop-blur">
          <NumerolojiAdminMobileSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
          <h1 className="text-lg font-semibold">Abonelik Planları</h1>
        </div>

        <div className="flex-1 p-6">
          {plansQuery.isLoading && <p className="text-sm text-muted-foreground">Planlar yükleniyor...</p>}

          {plans.map(plan => {
            const draft = getDraft(plan);
            const monthly = Number(draft.monthly_price ?? plan.monthly_price);
            const yearly = Number(draft.yearly_price ?? plan.yearly_price);
            const savings = calcSavings(monthly, yearly);
            const isDirty = Object.keys(draft).length > 0;
            const langTab = getLangTab(plan.id);
            const features = getFeatures(plan, langTab);
            const trFeaturesEmpty = getFeatures(plan, "tr").filter(f => f.trim()).length === 0;

            return (
              <div key={plan.id} className="mb-6 max-w-lg rounded-2xl border bg-background p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-base font-semibold">{plan.name}</h2>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.is_active ?? plan.is_active}
                      onChange={e => setField(plan.id, "is_active", e.target.checked)}
                      className="h-4 w-4 accent-violet-600"
                    />
                    Aktif
                  </label>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-1">
                    <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Aylık Fiyat (₺)</label>
                    <input
                      type="number"
                      min={0}
                      className="rounded-lg border bg-muted/30 px-3 py-2 text-sm"
                      value={draft.monthly_price ?? plan.monthly_price}
                      onChange={e => setField(plan.id, "monthly_price", Number(e.target.value))}
                    />
                  </div>

                  <div className="grid gap-1">
                    <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Yıllık Fiyat (₺)</label>
                    <input
                      type="number"
                      min={0}
                      className="rounded-lg border bg-muted/30 px-3 py-2 text-sm"
                      value={draft.yearly_price ?? plan.yearly_price}
                      onChange={e => setField(plan.id, "yearly_price", Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="mt-3 rounded-lg bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  Yıllık tasarruf: <span className="font-semibold text-foreground">%{savings}</span>
                  <span className="ml-2 text-xs">(otomatik hesaplanır)</span>
                </div>

                {/* Features */}
                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between">
                    <label className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Özellik Listesi
                    </label>
                    <div className="flex gap-1">
                      {(["tr", "en", "es"] as const).map(lang => (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => setLangTabs(prev => ({ ...prev, [plan.id]: lang }))}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                            langTab === lang
                              ? "bg-violet-600 text-white"
                              : "bg-muted/40 text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          {lang === "tr" ? "🇹🇷 TR" : lang === "en" ? "🇬🇧 EN" : "🇪🇸 ES"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {langTab !== "tr" && (
                    <p className="mb-2 text-xs text-muted-foreground">
                      Boş bırakılan satırlar mobil uygulamada otomatik olarak Türkçe'ye düşer.
                    </p>
                  )}

                  <div className="flex flex-col gap-2">
                    {features.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text"
                          className="flex-1 rounded-lg border bg-muted/30 px-3 py-1.5 text-sm"
                          value={f}
                          onChange={e => setFeature(plan.id, plan, langTab, idx, e.target.value)}
                          placeholder="Özellik açıklaması…"
                        />
                        <button
                          type="button"
                          onClick={() => removeFeature(plan.id, plan, langTab, idx)}
                          className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => addFeature(plan.id, plan, langTab)}
                    className="mt-2 flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-700"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Özellik Ekle
                  </button>
                </div>

                {trFeaturesEmpty && (
                  <p className="mt-2 text-xs text-destructive">
                    Türkçe özellik listesi boş olamaz — en az bir satır girin.
                  </p>
                )}

                {updateMut.isError && (
                  <p className="mt-2 text-xs text-destructive">{(updateMut.error as Error).message}</p>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    disabled={!isDirty || updateMut.isPending || trFeaturesEmpty}
                    onClick={() => handleSave(plan)}
                    className="flex items-center gap-2 rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40 hover:bg-violet-700"
                  >
                    <Save className="h-3.5 w-3.5" />
                    {updateMut.isPending ? "Kaydediliyor..." : "Kaydet"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
